<script setup lang="ts">
const meta = inject('meta', ref())
const { $t } = useI18n()
const { public: config } = useRuntimeConfig()
const status: string = 'PUBLISHED'
const strapiData = ref(null)
const locale = config.strapiLocale

async function fetchData() {
  const { data: response } = await useAsyncGraphqlQuery('customerServicePage', {
    documentId: meta.value.strapi_id,
    locale,
    status,
  }, useGraphqlDefaultClient())

  strapiData.value = response?.value?.data
}

await fetchData()

const customerService = computed(() => strapiData.value?.customerServicePage)
const customerServicePages = computed(() => strapiData.value?.customerServicePages?.filter((page: Record<string, unknown>) => page) || [])

const pageTitle = computed(
  () => customerService.value?.PageTitle || '',
)

const metaTitle = computed(
  () => customerService.value?.MetaData?.MetaTitle || pageTitle.value + ' | ' + $t('meta.title'),
)

const metaDescription = computed(
  () => customerService.value?.MetaData?.MetaDescription || $t('meta.description'),
)

const { setBreadcrumbs } = useBreadcrumbs()
setBreadcrumbs({
  label: pageTitle.value,
  parents: meta.value.parents,
})

const { pageView } = useDataLayer()
pageView('customer-service', metaTitle.value)

useHead({
  title: metaTitle,
  meta: [
    { name: 'description', content: metaDescription },
    { name: 'robots', content: customerService.value?.MetaData?.MetaRobots?.replace('_', ', ') },
  ],
  script: [
    { type: 'application/ld+json', key: 'RichSnippets', innerHTML: customerService.value?.MetaData?.RichSnippets || '' },
  ],
})
</script>

<template>
  <div class="customer-service">
    <StrapiBanner
      v-if="customerService?.Banner"
      :banner="customerService.Banner"
      :banner-count="1"
      :full-width="true"
      :lazy="false"
      class="customer-service__banner banner--hero"
    />

    <AppBreadcrumbs v-if="$route.path !== '/'" />

    <div class="customer-service__container">
      <div
        v-if="customerService"
        class="customer-service__layout"
      >
        <StrapiSlices
          v-if="customerService?.Slices?.length"
          class="customer-service__content"
          :slices="customerService.Slices"
        />

        <div
          v-else-if="customerServicePages?.length"
          class="customer-service__content"
        >
          <h2 class="customer-service__heading">
            Bekijk de veelgestelde vragen per onderwerp
          </h2>

          <ul class="customer-service__list">
            <li
              v-for="page in customerServicePages"
              :key="page"
              class="customer-service__item"
            >
              <AppLink
                :to="page?.PageUrl?.url"
                class="customer-service__link"
              >
                <span>{{ page?.PageTitle }}</span>
                <Icon name="icon:arrow" />
              </AppLink>

              <div class="customer-service__description">
                {{ page?.Description }}
              </div>
            </li>
          </ul>
        </div>

        <ServiceSupport class="customer-service__support" />
      </div>
    </div>

    <SlicesMobileStickyButton
      v-if="customerService?.MobileStickyButton"
      :button="customerService.MobileStickyButton"
    />
  </div>
</template>

<style>
@import url("~layers/app/pages/~customer-service.css");
</style>
